/**
 * UTM Parameter Tracking Utility
 * 
 * This utility handles:
 * - Extracting UTM parameters from URLs
 * - Storing UTM parameters in localStorage/sessionStorage
 * - Appending UTM parameters to links
 * - Tracking UTM data through the conversion funnel
 */

/**
 * Standard UTM parameters we track
 */
export const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content'
];

/**
 * Extract UTM parameters from current URL or provided URL string
 * @param {string} urlString - Optional URL string, defaults to window.location.search
 * @returns {Object} Object containing UTM parameters
 */
export const extractUTMParams = (urlString = null) => {
  const params = {};
  const searchParams = urlString 
    ? new URLSearchParams(urlString.includes('?') ? urlString.split('?')[1] : urlString)
    : new URLSearchParams(window.location.search);

  UTM_PARAMS.forEach(param => {
    const value = searchParams.get(param);
    if (value) {
      params[param] = decodeURIComponent(value);
    }
  });

  return params;
};

/**
 * Store UTM parameters in localStorage and sessionStorage
 * @param {Object} utmParams - UTM parameters object
 */
export const storeUTMParams = (utmParams) => {
  if (!utmParams || Object.keys(utmParams).length === 0) {
    return;
  }

  // Store in sessionStorage (cleared when browser tab closes)
  sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
  
  // Store in localStorage (persists across sessions)
  // We'll use this to track first touch attribution
  const existingFirstTouch = localStorage.getItem('utm_first_touch');
  if (!existingFirstTouch) {
    localStorage.setItem('utm_first_touch', JSON.stringify({
      ...utmParams,
      timestamp: new Date().toISOString()
    }));
  }

  // Also store last touch (for last-click attribution)
  localStorage.setItem('utm_last_touch', JSON.stringify({
    ...utmParams,
    timestamp: new Date().toISOString()
  }));
};

/**
 * Get stored UTM parameters from sessionStorage
 * @returns {Object} Stored UTM parameters
 */
export const getStoredUTMParams = () => {
  try {
    const stored = sessionStorage.getItem('utm_params');
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading stored UTM params:', error);
    return {};
  }
};

/**
 * Get first touch UTM parameters from localStorage
 * @returns {Object} First touch UTM parameters
 */
export const getFirstTouchUTMParams = () => {
  try {
    const stored = localStorage.getItem('utm_first_touch');
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading first touch UTM params:', error);
    return {};
  }
};

/**
 * Get last touch UTM parameters from localStorage
 * @returns {Object} Last touch UTM parameters
 */
export const getLastTouchUTMParams = () => {
  try {
    const stored = localStorage.getItem('utm_last_touch');
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading last touch UTM params:', error);
    return {};
  }
};

/**
 * Append UTM parameters to a URL
 * @param {string} url - Base URL
 * @param {Object} utmParams - UTM parameters to append (optional, defaults to stored params)
 * @param {boolean} useStored - Whether to use stored UTM params if none provided
 * @returns {string} URL with UTM parameters appended
 */
export const appendUTMToURL = (url, utmParams = null, useStored = true) => {
  if (!url) return url;

  const paramsToUse = utmParams || (useStored ? getStoredUTMParams() : {});
  
  if (!paramsToUse || Object.keys(paramsToUse).length === 0) {
    return url;
  }

  try {
    const urlObj = new URL(url);
    UTM_PARAMS.forEach(param => {
      if (paramsToUse[param]) {
        urlObj.searchParams.set(param, paramsToUse[param]);
      }
    });
    return urlObj.toString();
  } catch (error) {
    // If URL parsing fails (e.g., relative URL), append manually
    const separator = url.includes('?') ? '&' : '?';
    const paramString = UTM_PARAMS
      .filter(param => paramsToUse[param])
      .map(param => `${param}=${encodeURIComponent(paramsToUse[param])}`)
      .join('&');
    
    return paramString ? `${url}${separator}${paramString}` : url;
  }
};

/**
 * Initialize UTM tracking - call this when app loads
 * Extracts UTM params from URL and stores them
 */
export const initializeUTMTracking = () => {
  const utmParams = extractUTMParams();
  if (Object.keys(utmParams).length > 0) {
    storeUTMParams(utmParams);
    console.log('UTM parameters detected and stored:', utmParams);
  }
};

/**
 * Get UTM parameters formatted for analytics events
 * @param {Object} utmParams - Optional UTM params, defaults to stored
 * @returns {Object} Formatted for GA4/analytics
 */
export const getUTMForAnalytics = (utmParams = null) => {
  const params = utmParams || getStoredUTMParams();
  
  return {
    source: params.utm_source || '(direct)',
    medium: params.utm_medium || '(none)',
    campaign: params.utm_campaign || '(not set)',
    term: params.utm_term || '(not set)',
    content: params.utm_content || '(not set)',
  };
};

/**
 * Format UTM parameters for Firebase storage
 * @param {Object} utmParams - Optional UTM params, defaults to stored
 * @returns {Object} Formatted for Firebase
 */
export const getUTMForFirebase = (utmParams = null) => {
  const sessionParams = utmParams || getStoredUTMParams();
  const firstTouch = getFirstTouchUTMParams();
  const lastTouch = getLastTouchUTMParams();

  return {
    session: sessionParams,
    firstTouch: firstTouch,
    lastTouch: lastTouch,
    capturedAt: new Date().toISOString()
  };
};

